import { AnimatePresence, motion } from 'framer-motion';
import type Delta from 'quill-delta';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { AttachmentIcon, ChevronDownIcon } from '@treams/treams-icons';
import { TranscriptionStatus, TranscriptionUploadStatus } from '@treams/treams-network';
import {
	Button,
	FlexGap,
	FormattedTranscript,
	IconWrapper,
	JunkIcon,
	Modal,
	Paragraph,
	Popover,
	type TranscriptionSpeakerInfo,
	tokens,
} from '@treams/treams-ui';

import { AudioPlayer } from './AudioPlayer';
import { type StreamingState, appendQueuedChunks } from './useStreamingTranscription';

const STICK_TO_BOTTOM_THRESHOLD_PX = 8;

const StyledButton = styled(Button)`
	font-weight: normal;
`;

const TranscriptText = styled(Box)`
	line-height: ${tokens.typography.lineHeights.body};
	border-radius: ${tokens.radius.sm}px;
	padding: ${tokens.padding.compact}px ${tokens.padding.container}px;
	background-color: ${tokens.brand.background};
	border: 1px solid ${tokens.brand.border};
	font-size: ${tokens.typography.sizes.small}px;
	height: 240px;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.inline}px;
`;

const LiveStatus = styled.div`
	color: ${tokens.text.tertiary};
	font-size: ${tokens.typography.sizes.small}px;
`;

const LivePartial = styled.div`
	color: ${tokens.text.secondary};
`;

const Silence = styled.span`
	color: ${tokens.text.tertiary};
	opacity: 0.6;
	font-style: italic;
`;

const Warning = styled.div`
	color: ${tokens.warning.text};
`;

type StreamingView = Pick<
	StreamingState,
	| 'phase'
	| 'assemblyStatus'
	| 'syncStatus'
	| 'failure'
	| 'transcript'
	| 'unsavedChunks'
	| 'partial'
	| 'silent'
>;

function pickStreamingView(state?: StreamingState): StreamingView | undefined {
	if (!state || state.phase === 'idle') return undefined;

	return {
		phase: state.phase,
		assemblyStatus: state.assemblyStatus,
		syncStatus: state.syncStatus,
		failure: state.failure,
		transcript: state.transcript,
		unsavedChunks: state.unsavedChunks,
		partial: state.partial,
		silent: state.silent,
	};
}

function streamingStatusText(view: StreamingView, t: (key: string) => string): string {
	if (view.phase === 'finishing') return t('transcription.streaming.status.finishing');
	if (view.phase === 'cancelling') return t('transcription.streaming.status.cancelling');
	if (
		view.phase === 'starting' ||
		view.phase === 'countdown' ||
		view.assemblyStatus === 'connecting'
	) {
		return t('transcription.streaming.status.connecting');
	}
	if (view.phase === 'recording') {
		return view.assemblyStatus === 'reconnecting'
			? t('transcription.streaming.status.reconnecting')
			: t('transcription.streaming.status.transcribing');
	}
	if (view.syncStatus === 'pending' || view.syncStatus === 'syncing') {
		return t('transcription.streaming.status.saving');
	}
	if (view.failure) return t(`transcription.streaming.failure.${view.failure}`);

	return t('transcription.streaming.status.paused');
}

type TranscriptionData = {
	id: string;
	status: TranscriptionStatus;
	uploadStatus: TranscriptionUploadStatus;
	transcript?: string | null;
	summary?: string | null;
	editableSummary?: Delta;
	audioUrl?: string | null;
	speakers?: TranscriptionSpeakerInfo[] | null;
	createdAt: string;
};

type Props = {
	transcription?: TranscriptionData | null;
	onRemove?: () => void;
	removing?: boolean;
	canRemove?: boolean;
	label?: string;
	streamingState?: StreamingState;
};

export const TranscriptionAttachment = (props: Props) => {
	const { t } = useTranslation();
	const [confirmingRemove, setConfirmingRemove] = useState(false);
	const transcriptContainerRef = useRef<HTMLDivElement>(null);
	const stickToBottom = useRef(true);

	const streamingView = pickStreamingView(props.streamingState);
	const isStreamingActive = !!streamingView;
	const isRecording =
		streamingView?.phase === 'starting' ||
		streamingView?.phase === 'countdown' ||
		streamingView?.phase === 'recording';

	const isLoading =
		!isStreamingActive &&
		(!props.transcription ||
			props.transcription.uploadStatus === TranscriptionUploadStatus.UploadPending ||
			props.transcription.status === TranscriptionStatus.Pending ||
			props.transcription.status === TranscriptionStatus.Processing);

	const hasSummary =
		!isStreamingActive &&
		!isLoading &&
		props.transcription?.status === TranscriptionStatus.Completed &&
		(!!props.transcription.editableSummary || !!props.transcription.summary);

	const [expanded, setExpanded] = useState(isRecording);
	const [prevRecording, setPrevRecording] = useState(isRecording);
	if (isRecording !== prevRecording) {
		setPrevRecording(isRecording);
		if (isRecording) setExpanded(true);
	}

	const [prevHasSummary, setPrevHasSummary] = useState(hasSummary);
	if (hasSummary !== prevHasSummary) {
		setPrevHasSummary(hasSummary);
		if (hasSummary) setExpanded(false);
	}

	const savedTranscript = props.transcription?.transcript ?? '';
	const transcript = streamingView
		? (streamingView.transcript ?? appendQueuedChunks(savedTranscript, streamingView.unsavedChunks))
		: savedTranscript;
	const hasTranscript = !isLoading && !!transcript;
	const hasAudio = !isLoading && !!props.transcription?.audioUrl;
	const canExpand = hasTranscript || isStreamingActive;
	const isEmptyTranscription = !isLoading && !canExpand && !hasAudio;
	const label = isLoading
		? t('transcription.processing')
		: (props.label ?? t('transcription.meetingTranscription'));

	const removeAllowed = props.canRemove && props.onRemove && !!props.transcription;

	const notCapturing =
		streamingView?.phase === 'recording' && streamingView.assemblyStatus === 'reconnecting';
	const showSilence =
		streamingView?.phase === 'recording' &&
		streamingView.silent &&
		!notCapturing &&
		!streamingView.partial;

	useLayoutEffect(() => {
		const element = transcriptContainerRef.current;
		if (element && stickToBottom.current) {
			element.scrollTop = element.scrollHeight;
		}
	}, [
		expanded,
		transcript,
		streamingView?.partial,
		streamingView?.phase,
		streamingView?.assemblyStatus,
		showSilence,
	]);

	return (
		<FlexGap gap={8} flexDirection="column">
			{isEmptyTranscription ? (
				<FlexGap gap={12} flexDirection="column" alignItems="flex-start">
					<Paragraph small>{t('transcription.emptyTranscriptHint')}</Paragraph>
					{removeAllowed && (
						<Button
							type="ai"
							small
							title={t('transcription.emptyTranscriptRemoveAction')}
							onClick={() => {
								void props.onRemove?.();
							}}
							loading={props.removing}
						/>
					)}
				</FlexGap>
			) : (
				<FlexGap gap={8} alignItems="center" justifyContent="space-between">
					<StyledButton
						type="ai"
						title={
							<FlexGap gap={4} alignItems="center">
								<div>{label}</div>
								{canExpand && (
									<IconWrapper
										size={20}
										color="primary"
										mt={1}
										style={{
											transform: expanded ? 'rotate(180deg)' : 'none',
											transition: 'transform 0.2s ease',
										}}
									>
										<ChevronDownIcon />
									</IconWrapper>
								)}
							</FlexGap>
						}
						width="fit-content"
						icon={
							<IconWrapper size={isLoading ? 16 : 20} style={{ scale: isLoading ? 1 : 0.8 }}>
								<AttachmentIcon />
							</IconWrapper>
						}
						disabled={isLoading}
						onClick={canExpand ? () => setExpanded((prev) => !prev) : undefined}
					/>
					{removeAllowed && (
						<Button
							type="destructive"
							small
							icon={<JunkIcon />}
							title={t('transcription.deleteRecording')}
							onClick={() => setConfirmingRemove(true)}
							disabled={props.removing}
						/>
					)}
				</FlexGap>
			)}
			{removeAllowed && !isEmptyTranscription && (
				<Popover center visible={confirmingRemove}>
					<Modal>
						<Modal.Header
							title={t('common.areYouSure')}
							onClose={() => setConfirmingRemove(false)}
						/>
						<Modal.Contents>
							<Paragraph>{t('transcription.confirmRemove')}</Paragraph>
						</Modal.Contents>
						<Modal.FormFooter
							onCancel={() => setConfirmingRemove(false)}
							onSave={props.onRemove}
							saveTitle={t('common.confirm')}
							disabled={props.removing}
						/>
					</Modal>
				</Popover>
			)}
			<AnimatePresence>
				{expanded && canExpand && (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: 'auto' }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.2 }}
						style={{ overflow: 'hidden' }}
					>
						<FlexGap gap={8} flexDirection="column">
							{hasAudio && <AudioPlayer src={props.transcription!.audioUrl!} />}
							<TranscriptText
								ref={transcriptContainerRef}
								mb="8px"
								onScroll={(event) => {
									const element = event.currentTarget;
									stickToBottom.current =
										element.scrollTop + element.clientHeight >=
										element.scrollHeight - STICK_TO_BOTTOM_THRESHOLD_PX;
								}}
							>
								{hasTranscript && (
									<FormattedTranscript text={transcript} speakers={props.transcription?.speakers} />
								)}
								{streamingView && (
									<>
										{notCapturing && (
											<Warning>{t('transcription.streaming.status.notCapturing')}</Warning>
										)}
										{(streamingView.partial || showSilence) && (
											<LivePartial>
												{streamingView.partial || (
													<Silence>{t('transcription.streaming.silence')}</Silence>
												)}
											</LivePartial>
										)}
										<LiveStatus>{streamingStatusText(streamingView, t)}</LiveStatus>
									</>
								)}
							</TranscriptText>
						</FlexGap>
					</motion.div>
				)}
			</AnimatePresence>
		</FlexGap>
	);
};
